import { useCallback, useEffect, useRef, useState } from "react";

import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";
import { useDispatch, useSelector } from "react-redux";

import { DotsLoader } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import { useMount } from "app/custom-hooks/useMount";
import { useUnmount } from "app/custom-hooks/useUnmount";
import MessagesList from "app/pages/sales-intelligence/modules/ai-agent/components/MessagesList/MessagesList";
import type { ChatUiContext } from "app/pages/sales-intelligence/modules/ai-agent/components/MessagesList/types";
import { useChatScrolling } from "app/pages/sales-intelligence/modules/ai-agent/hooks/useChatScrolling";
import { aiAgentQueryKeys } from "app/pages/sales-intelligence/modules/ai-agent/queries/queryKeyFactory";
import useSuperAgentQuery from "app/pages/sales-intelligence/modules/ai-agent/queries/useSuperAgentQuery";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import {
    addHumanMessage,
    clearAiAgentChatState,
    clearChatText,
    selectChatMessages,
    selectChatText,
    selectUserMessageCount,
    setChatText,
    setMessages,
} from "app/pages/sales-intelligence/modules/ai-agent/store";
import { SuperAgentChatPageVariant } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/components/SuperAgentChatPageVariant/SuperAgentChatPageVariant";
import { SuperAgentGatePageVariant } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/components/SuperAgentGatePageVariant/SuperAgentGatePageVariant";
import { SuperAgentMessage } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/components/SuperAgentMessage/SuperAgentMessage";
import { useGetThreadById } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/queries/useGetThreadById";
import type { ThreadMessage } from "app/pages/sales-intelligence/modules/ai-agent/types/thread";
import AIAgentReasoning from "app/pages/sales-intelligence/pages/ai-agent/components/AIAgentReasoning/AIAgentReasoning";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";
import ContactIssueModalContainer from "app/pages/sales-intelligence/sub-modules/contacts-search/components/ContactIssueModal/ContactIssueModalContainer";
import ContactsExportPreviewModal from "app/pages/sales-intelligence/sub-modules/contacts-search/components/ContactsTableActionsPanel/ExportContactsToCrm/ContactsExportPreviewModal";
import { LoadingModal } from "app/pages/sales-intelligence/sub-modules/contacts-search/components/ContactsTableActionsPanel/ExportContactsToCrm/LoadingModal/LoadingModal";
import { RevealBeforeExportModal } from "app/pages/sales-intelligence/sub-modules/contacts-search/components/RevealBeforeExportModal/RevealBeforeExportModal";
import RevealLimitReachedModalContainer from "app/pages/sales-intelligence/sub-modules/contacts-search/components/RevealLimitReachedModal/RevealLimitReachedModalContainer";
import useRemainingRevealCredits from "app/pages/sales-intelligence/sub-modules/contacts-search/hooks/useRemainingRevealCredits";
import useSingleContactCrmExport from "app/pages/sales-intelligence/sub-modules/contacts-search/hooks/useSingleContactCrmExport";
import { proNavigator } from "single-spa/react-app/ProNavigator";

const UI_CONTEXT: ChatUiContext = {
    chatTheme: "super-agent",
    loader: (
        <AIAgentReasoning
            textKeys={[
                "si.super-agent.reasoning.text1",
                "si.super-agent.reasoning.text2",
                "si.super-agent.reasoning.text3",
                "si.super-agent.reasoning.text4",
                "si.super-agent.reasoning.text5",
            ]}
        />
    ),
    streamingLoader: (
        <Box margin="12px 0 0 35px">
            <DotsLoader />
        </Box>
    ),
    markdownComponent: SuperAgentMessage,
};

export const AISuperAgentPageContent = () => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const {
        params: { id: threadId, autogenerate },
    } = useCurrentStateAndParams<{ id?: string; autogenerate?: string }>();

    const [pageVariant, setPageVariant] = useState<"gate" | "chat">(threadId ? "chat" : "gate");
    const [activePrompt, setActivePrompt] = useState("");

    const chatText = useSelector(selectChatText);
    const messages = useSelector(selectChatMessages);

    const {
        onExportToCRM,
        isRowMatching,
        isRowPreviewing,
        integrationName,
        onConfirmCrmExport,
        onCancelCrmPreview,
        onCancelCrmLoading,
        isRevealModalOpen,
        onConfirmRevealExport,
        onCloseRevealModal,
    } = useSingleContactCrmExport({});

    const remainingCredits = useRemainingRevealCredits();

    const userMessageCount = useSelector(selectUserMessageCount);

    const isNewlyCreatedThread = useRef(false);

    const lastUserMessageRef = useRef<HTMLDivElement>(null);
    const lastAiMessageRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const bottomRef = useRef<HTMLDivElement>(null);

    const { data: threadData } = useGetThreadById({
        threadId,
        enabled: !isNewlyCreatedThread.current && !!threadId,
    });

    const onThreadCreated = useCallback(
        (id: string) => {
            isNewlyCreatedThread.current = true;
            proNavigator.updateQueryParams({ id }, { reload: false });
            void queryClient.invalidateQueries({
                queryKey: aiAgentQueryKeys.superAgent.recents(),
                refetchType: "all",
            });
        },
        [queryClient],
    );

    const { scrollChatWhenAiResponse, scrollUserMessageToTop, scrollToBottom } = useChatScrolling({
        containerRef,
        lastAiMessageRef,
        lastUserMessageRef,
        bottomRef,
    });

    useEffect(() => {
        if (threadData?.messages) {
            dispatch(setMessages(threadData.messages));
            setTimeout(() => {
                scrollToBottom();
            }, 0);
        }
    }, [threadData, dispatch, scrollToBottom]);

    const { data, messageId, stop } = useSuperAgentQuery({
        message: activePrompt,
        threadId,
        onThreadCreated,
        questionId: userMessageCount,
        fetcher: threadId
            ? salesApiService.aiAgent.superAgent.postSuperAgentMessage
            : salesApiService.aiAgent.superAgent.createSuperAgentThread,
    });

    const chatFetchingCount = useIsFetching({
        queryKey: aiAgentQueryKeys.superAgent.chat(userMessageCount.toString()),
        exact: true,
    });
    const isLoading = chatFetchingCount > 0;

    useEffect(() => {
        if (data && messageId) {
            const updatedMessages: ThreadMessage[] = activePrompt
                ? [...messages, { type: "ai", content: data, id: messageId }]
                : messages.map((msg, idx) =>
                      idx === messages.length - 1 ? { type: "ai", content: data, id: messageId } : msg,
                  );
            dispatch(setMessages(updatedMessages));
            setActivePrompt("");
            requestAnimationFrame(() => scrollChatWhenAiResponse());
        }
    }, [data]);

    useUnmount(() => {
        dispatch(clearAiAgentChatState());
    });

    const updateChatText = (value: string) => {
        dispatch(setChatText(value));
    };

    const handleChatReset = () => {
        if (isLoading) {
            void stop();
        }
        setPageVariant("gate");
        setActivePrompt("");

        proNavigator.updateQueryParams({ id: undefined }, { reload: false });
        dispatch(clearAiAgentChatState());

        AIAgentTrackingService.superAgent.trackNewChatClick();
    };

    const handleSubmit = () => {
        if (isLoading) {
            return;
        }

        const message = chatText.trim();

        dispatch(addHumanMessage(message));
        setActivePrompt(message);
        dispatch(clearChatText());

        requestAnimationFrame(() => {
            scrollUserMessageToTop(0);
        });
    };

    useMount(() => {
        if (chatText && autogenerate) {
            setPageVariant("chat");
            handleSubmit();
        }
    });

    const handleGatePageSubmit = () => {
        if (!chatText) {
            return;
        }

        AIAgentTrackingService.superAgent.trackSendMessage(chatText, "gate");

        setPageVariant("chat");
        handleSubmit();
    };

    const handleChatPageSubmit = () => {
        if (!chatText) {
            return;
        }

        AIAgentTrackingService.superAgent.trackSendMessage(chatText, "chat");
        handleSubmit();
    };

    const isLastMessageHuman = messages.length > 0 ? messages[messages.length - 1].type === "human" : false;

    return pageVariant === "gate" ? (
        <SuperAgentGatePageVariant
            handleSubmit={handleGatePageSubmit}
            value={chatText}
            setValue={updateChatText}
        />
    ) : (
        <>
            <SuperAgentChatPageVariant
                value={chatText}
                setValue={updateChatText}
                handleSubmit={handleChatPageSubmit}
                isLoading={isLoading}
                messagesContainerRef={containerRef}
                messagesBottomRef={bottomRef}
                handleChatReset={handleChatReset}
            >
                <MessagesList
                    uiContext={UI_CONTEXT}
                    isLoading={isLoading}
                    messages={messages}
                    lastUserMessageRef={lastUserMessageRef}
                    lastAiMessageRef={lastAiMessageRef}
                    renderLoaderCondition={Boolean(isLoading && isLastMessageHuman)}
                    isStreamingLoaderVisible={Boolean(isLoading && !isLastMessageHuman)}
                    withControls={false}
                    mdComponentProps={{ onExportToCRM }}
                />
            </SuperAgentChatPageVariant>
            <RevealLimitReachedModalContainer />
            <ContactIssueModalContainer />
            <RevealBeforeExportModal
                isOpen={isRevealModalOpen}
                unrevealedCount={1}
                revealedCount={0}
                remainingCredits={remainingCredits}
                onConfirm={onConfirmRevealExport}
                onClose={onCloseRevealModal}
            />
            {integrationName ? (
                <ContactsExportPreviewModal
                    isOpen={isRowPreviewing}
                    integrationName={integrationName}
                    onExport={onConfirmCrmExport}
                    onCancel={onCancelCrmPreview}
                />
            ) : null}
            <LoadingModal
                isOpen={isRowMatching}
                onClose={onCancelCrmLoading}
            />
        </>
    );
};
